import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import { User, Briefcase, MapPin, Loader2, Clock, ChevronRight } from 'lucide-react';

const statusTabs = [
  { key: '',                label: 'All',      color: 'bg-slate-900 text-white' },
  { key: 'PENDING_APPROVAL', label: 'Pending',  color: 'bg-yellow-500 text-white' },
  { key: 'ACTIVE',           label: 'Active',   color: 'bg-green-600 text-white' },
  { key: 'APPROVED',         label: 'Approved', color: 'bg-teal-600 text-white' },
  { key: 'REJECTED',         label: 'Rejected', color: 'bg-red-600 text-white' },
];

const statusBadge = {
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
  ACTIVE:           'bg-green-100 text-green-700',
  APPROVED:         'bg-teal-100 text-teal-700',
  REJECTED:         'bg-red-100 text-red-700',
};

export default function AllStaff() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAllStaff(filter || undefined, 0, 50);
      const data = res.data.data;
      setStaff(data.content || []);
      setTotal(data.totalElements || 0);
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav title="All Staff" subtitle={`${total} registered`} />

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Status tabs */}
        <div className="flex gap-1.5 flex-wrap mb-6">
          {statusTabs.map((tab) => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition
                ${filter === tab.key
                  ? 'bg-slate-900 text-white shadow'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100">
            <User className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 font-semibold">No staff found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {staff.map((s) => (
              <div key={s.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5
                  flex items-center justify-between gap-4 cursor-pointer hover:shadow-md transition"
                onClick={() => navigate(`/admin/staff/${s.id}/review`)}>
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl
                    flex items-center justify-center text-white font-black text-base shrink-0">
                    {s.firstName?.charAt(0)}{s.lastName?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-slate-800">{s.firstName} {s.lastName}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold
                        ${statusBadge[s.status] || 'bg-gray-100 text-gray-600'}`}>
                        {s.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-500">
                      {s.staffTypeName && (
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3" /> {s.staffTypeName}
                        </span>
                      )}
                      {s.city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {s.city}{s.state ? `, ${s.state}` : ''}
                        </span>
                      )}
                      {s.experienceYears != null && (
                        <span>{s.experienceYears} yrs exp</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{s.phone}</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}